/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  IconPackage,
  // IconTruckDelivery,
  IconCheck,
  IconLoader2,
  IconCopy,
} from "@tabler/icons-react";
import {
  useFetchWebhookDetails,
  useWebhookRegisterMutation,
} from "../../../apis/settingsApis";
import { useProfileData } from "../../../apis/profileApis";
import toast from "react-hot-toast";

const Webhooks: React.FC = () => {
  // 1. Local State
  const [webhookUrl, setWebhookUrl] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [webhookRegistered, setWebhookRegistered] = useState<boolean>(false);

  // 2. API Hooks
  const { mutate: registerWebhook, isPending: isSubmitting } =
    useWebhookRegisterMutation();
  const {
    data: webhookDetails,
    isPending: isFetching,
    refetch,
  } = useFetchWebhookDetails();
  const { data: userData } = useProfileData();

  // 3. Derived State
  const isActive =
    (typeof webhookDetails === "object" && !!webhookDetails?.webhookUrl) ||
    webhookRegistered;

  const merchantId =
    (typeof webhookDetails === "object" && webhookDetails?.merchantId) ||
    (typeof webhookDetails === "object" && webhookDetails?.clientId) ||
    userData?.clientId ||
    userData?.id ||
    "";

  const secretKey =
    (typeof webhookDetails === "object" && webhookDetails?.secretToken) ||
    userData?.secretKey ||
    "";

  // Sync server data to local state on load or when webhookDetails updates
  useEffect(() => {
    if (webhookDetails && typeof webhookDetails === "object") {
      setWebhookUrl(webhookDetails?.webhookUrl || "");
      setSelectedGroups(
        Array.isArray(webhookDetails?.events) ? webhookDetails.events : [],
      );
    }
  }, [webhookDetails]);

  useEffect(() => {
    refetch();
  }, []);

  // Check if current form differs from saved server data
  const savedUrl =
    typeof webhookDetails === "object" ? webhookDetails?.webhookUrl || "" : "";
  const savedEvents =
    typeof webhookDetails === "object" && Array.isArray(webhookDetails?.events)
      ? webhookDetails.events
      : [];

  const hasChanges =
    webhookUrl !== savedUrl ||
    JSON.stringify([...selectedGroups].sort()) !==
      JSON.stringify([...savedEvents].sort());

  const isFormInvalid =
    !webhookUrl.trim() ||
    !webhookUrl.startsWith("https://") ||
    selectedGroups.length === 0;

  const isButtonDisabled =
    isFormInvalid || isSubmitting || (isActive && !hasChanges);

  const eventGroups = [
    {
      id: "ORDER_UPDATES",
      title: "Order Status Updates",
      description:
        "Covers the entire lifecycle from creation to final delivery.",
      icon: <IconPackage size={22} className="text-[#212121]" />,
      statuses: [
        { code: "NEW_ORDER", desc: "Order created in system" },
        { code: "CANCELLED", desc: "Order voided by merchant" },
        { code: "PICKUP_GENERATED", desc: "Manifest created" },
        { code: "READY_FOR_PICKUP", desc: "Assigned to driver" },
        { code: "PICKUP_COLLECTED", desc: "Scanned by courier" },
        { code: "RECEIVE_IN_WAREHOUSE", desc: "Arrived at hub" },
        { code: "OUT_FOR_DELIVERY", desc: "With delivery agent" },
        { code: "DELIVERED", desc: "Handed to receiver" },
        { code: "NOT_DELIVERED", desc: "Delivery failed" },
        { code: "RETURNED_TO_SHIPPER", desc: "Returned to sender" },
      ],
    },
    // {
    //   id: "PRODUCT_UPDATES",
    //   title: "Product Tracking Updates",
    //   comingSoon: true,
    //   description: "Catalog management and inventory quantity shifts.",
    //   icon: <IconTruckDelivery size={22} className="text-[#212121]" />,
    //   statuses: [
    //     { code: "CREATE", desc: "Product created in system" },
    //     { code: "UPDATE", desc: "Product updated in system" },
    //     { code: "DELETE", desc: "Product removed from system" },
    //     { code: "STATUS", desc: "Status toggled (Active/Inactive)" },
    //     { code: "QTY_UPDATE", desc: "Stock levels modified" },
    //   ],
    // },
  ];

  const toggleGroup = (groupId: string, comingSoon?: boolean) => {
    if (comingSoon) return;
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((g) => g !== groupId)
        : [...prev, groupId],
    );
  };

  const handleSubmit = () => {
    const payload = {
      webhook_url: webhookUrl,
      subscriptions: selectedGroups,
      enabled: true,
      deployed_at: new Date().toISOString(),
    };

    registerWebhook(payload, {
      onSuccess: (savedData: any) => {
        setWebhookRegistered(true);
        if (savedData && typeof savedData === "object") {
          setWebhookUrl(savedData?.webhookUrl || webhookUrl);
          setSelectedGroups(
            Array.isArray(savedData?.events)
              ? savedData.events
              : selectedGroups,
          );
        }
        refetch();
        toast.success(
          isActive
            ? "Configuration Updated"
            : "Webhook Registered Successfully",
        );
      },
      onError: (error: any) => {
        toast.error(error?.message || "Action failed");
      },
    });
  };

  if (isFetching) {
    return (
      <div className="w-[47rem] mx-auto h-64 flex flex-col items-center justify-center py-4">
        <IconLoader2 className="animate-spin text-[#FBD037] mb-3" size={36} />
        <p className="text-[#212121]/60 text-sm font-medium">
          Loading webhook configuration...
        </p>
      </div>
    );
  }

  return (
    <div className="w-[47rem] mx-auto text-[#1E293B] space-y-8 py-4">
      {/* Header matching project theme */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <p className="text-xs font-semibold text-[#212121]/70 uppercase tracking-wide">
            WEBHOOK CONFIGURATION
          </p>
          {isActive && (
            <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              Active
            </span>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          className="bg-[#FBD037] hover:bg-[#ebd030] disabled:opacity-50 disabled:cursor-not-allowed text-[#212121] text-sm font-semibold px-5 py-1.5 rounded-full transition-all flex items-center gap-1.5 shadow-sm"
        >
          {isSubmitting ? (
            <>
              <IconLoader2 className="animate-spin" size={16} />
              <span>Saving...</span>
            </>
          ) : isActive ? (
            hasChanges ? (
              "Save Changes"
            ) : (
              "Saved"
            )
          ) : (
            "Enable"
          )}
        </button>
      </div>

      {/* Form Fields dividing list matching Address & Password tabs */}
      <div className="divide-y divide-[#212121]/20 space-y-4">
        {/* Webhook URL Input */}
        <div className="flex justify-between items-center space-y-4">
          <label className="w-full text-base text-[#212121]">Webhook URL</label>
          <div className="w-full">
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://api.yourdomain.com/webhooks"
              className={`px-4 py-2 text-sm w-full rounded-full outline-none bg-white border ${
                webhookUrl && !webhookUrl.startsWith("https://")
                  ? "border-red-400 focus:border-red-500"
                  : "border-[#D8D8D8] focus:border-[#FBD037]"
              }`}
            />
            {webhookUrl && !webhookUrl.startsWith("https://") && (
              <p className="text-xs text-red-500 mt-1 pl-3">
                Endpoint must start with https://
              </p>
            )}
          </div>
        </div>

        {/* Merchant ID */}
        {merchantId && (
          <div className="flex justify-between items-center space-y-4 pt-2">
            <label className="w-full text-base text-[#212121]">Merchant ID</label>
            <div className="w-full relative flex items-center">
              <input
                type="text"
                value={merchantId}
                readOnly
                className="px-4 py-2 pr-10 text-xs font-mono w-full rounded-full outline-none bg-[#F2F2F2] border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(merchantId);
                  toast.success("Merchant ID copied to clipboard");
                }}
                title="Copy Merchant ID"
                className="absolute right-3 text-gray-500 hover:text-black transition-colors cursor-pointer"
              >
                <IconCopy size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Secret Key */}
        {secretKey && (
          <div className="flex justify-between items-center space-y-4 pt-2">
            <label className="w-full text-base text-[#212121]">
              Secret Key
            </label>
            <div className="w-full relative flex items-center">
              <input
                type="text"
                value={secretKey}
                readOnly
                className="px-4 py-2 pr-10 text-xs font-mono w-full rounded-full outline-none bg-[#F2F2F2] border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(secretKey);
                  toast.success("Secret key copied to clipboard");
                }}
                title="Copy Secret Key"
                className="absolute right-3 text-gray-500 hover:text-black transition-colors cursor-pointer"
              >
                <IconCopy size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Subscription Events Section */}
        <div className="pt-4 space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-base text-[#212121]">
              Subscription Events
            </label>
            <span className="text-xs text-[#212121]/60">
              Select events to receive
            </span>
          </div>

          <div className="space-y-3">
            {eventGroups.map((group) => {
              const isSelected = selectedGroups.includes(group.id);
              const isComingSoon = (group as any).comingSoon;
              return (
                <div
                  key={group.id}
                  onClick={() =>
                    !isComingSoon && toggleGroup(group.id, isComingSoon)
                  }
                  className={`group relative flex items-center justify-between p-4 rounded-2xl border transition-all ${
                    isComingSoon
                      ? "bg-[#F9FAFB] border-[#E5E7EB] opacity-60 cursor-not-allowed"
                      : isSelected
                      ? "bg-white border-[#FBD037] shadow-xs cursor-pointer"
                      : "bg-white border-[#E5E7EB] hover:border-[#CBD5E1] cursor-pointer"
                  }`}
                >
                  <div className="flex items-center gap-3.5">
                    <div
                      className={`p-2.5 rounded-xl border ${
                        isSelected
                          ? "bg-[#FBD037]/20 border-[#FBD037]/40 text-[#212121]"
                          : "bg-gray-50 border-gray-200 text-gray-500"
                      }`}
                    >
                      {group.icon}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm text-[#212121]">
                          {group.title}
                        </h4>
                        {isComingSoon && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200 uppercase tracking-wider">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#212121]/60 mt-0.5">
                        {group.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Hover Tooltip for Status Codes */}
                    <div className="opacity-0 group-hover:opacity-100 absolute bottom-full right-0 mb-3 w-72 p-4 bg-[#212121] text-white rounded-2xl shadow-2xl pointer-events-none transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                      <p className="text-[10px] font-black uppercase tracking-widest text-[#FBD037] mb-2">
                        Technical Statuses:
                      </p>
                      <div className="space-y-1.5">
                        {group.statuses.map((s) => (
                          <div
                            key={s.code}
                            className="flex justify-between text-[11px] border-b border-gray-700/60 pb-1 last:border-0"
                          >
                            <span className="font-mono font-bold text-[#FBD037]">
                              {s.code}
                            </span>
                            <span className="opacity-75 italic text-gray-300">
                              {s.desc}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="absolute bottom-[-6px] right-8 w-3 h-3 bg-[#212121] rotate-45"></div>
                    </div>

                    {isComingSoon ? (
                      <span className="text-[11px] font-medium text-gray-400">
                        Unavailable
                      </span>
                    ) : isSelected ? (
                      <div className="w-6 h-6 rounded-full bg-[#FBD037] flex items-center justify-center text-[#212121] shadow-xs">
                        <IconCheck size={14} stroke={3} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full border-2 border-[#D8D8D8] bg-white" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Note section matching Password tab */}
        <div className="pt-3">
          <p className="text-sm px-2 text-[#212121] border-l-4 border-[#FBD037]">
            <b>Note</b> : Webhook updates are dispatched via <b>POST</b> with a
            JSON payload. Ensure your endpoint is publicly accessible over HTTPS
            and returns <b>200 OK</b>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Webhooks;
